/**
 * Responsibility: Implement vitals math and normalization for shared engine/UI use.
 * Scope: Owns scalar/max vital contracts only; orchestration remains elsewhere.
 */
import type { Attribute, AttributeKey, Vitals } from "@anyquest/shared-core"

export const VITAL_KEYS = ['vitality', 'spirit', 'essence'] as const
export type VitalKey = typeof VITAL_KEYS[number]
export type VitalTone = 'high' | 'mid' | 'low'

const VITAL_MAX_KEYS: Record<VitalKey, keyof Vitals> = {
  vitality: 'maxVitality',
  spirit: 'maxSpirit',
  essence: 'maxEssence',
}

function clampWhole(value: unknown, fallback: number, min = 0, max = Number.POSITIVE_INFINITY): number {
  const n = Number(value)
  if (!Number.isFinite(n)) return fallback
  return Math.max(min, Math.min(max, Math.round(n)))
}

function attr(attributes: Record<AttributeKey, Attribute>, key: AttributeKey): number {
  return clampWhole(attributes?.[key]?.val, 10, 1, 100)
}

export function deriveVitalMaximums(attributes: Record<AttributeKey, Attribute>): Pick<Vitals, 'maxVitality' | 'maxSpirit' | 'maxEssence'> {
  const maxVitality = 24 + (attr(attributes, 'CON') * 4) + (attr(attributes, 'FOR') * 3)
  const maxSpirit = 18 + (attr(attributes, 'WIL') * 4) + (attr(attributes, 'WIS') * 2) + attr(attributes, 'CHA')
  const maxEssence = 14 + (attr(attributes, 'INT') * 3) + (attr(attributes, 'WIL') * 2) + (attr(attributes, 'PRS') * 2)
  return {
    maxVitality: clampWhole(maxVitality, 94, 1, 999),
    maxSpirit: clampWhole(maxSpirit, 88, 1, 999),
    maxEssence: clampWhole(maxEssence, 84, 1, 999),
  }
}

export function getVitalMax(vitals: Pick<Vitals, VitalKey | keyof Pick<Vitals, 'maxVitality' | 'maxSpirit' | 'maxEssence'>>, key: VitalKey): number {
  const rawMax = vitals?.[VITAL_MAX_KEYS[key]]
  const legacyFallback = Math.max(100, clampWhole(vitals?.[key], 100, 0, 999))
  return clampWhole(rawMax, legacyFallback, 1, 999)
}

export function clampVitalValue(value: unknown, max: number, fallback = max): number {
  return clampWhole(value, fallback, 0, Math.max(1, max))
}

export function normalizeVitals(
  raw: Partial<Vitals> | null | undefined,
  attributes: Record<AttributeKey, Attribute>,
  fallback?: Partial<Vitals>,
): Vitals {
  const source = raw ?? {}
  const fallbackSource = fallback ?? {}
  const maxima = deriveVitalMaximums(attributes)
  const maxVitality = clampWhole(source.maxVitality, clampWhole(fallbackSource.maxVitality, maxima.maxVitality, 1, 999), 1, 999)
  const maxSpirit = clampWhole(source.maxSpirit, clampWhole(fallbackSource.maxSpirit, maxima.maxSpirit, 1, 999), 1, 999)
  const maxEssence = clampWhole(source.maxEssence, clampWhole(fallbackSource.maxEssence, maxima.maxEssence, 1, 999), 1, 999)
  return {
    vitality: clampVitalValue(source.vitality, maxVitality, clampVitalValue(fallbackSource.vitality, maxVitality, maxVitality)),
    spirit: clampVitalValue(source.spirit, maxSpirit, clampVitalValue(fallbackSource.spirit, maxSpirit, maxSpirit)),
    essence: clampVitalValue(source.essence, maxEssence, clampVitalValue(fallbackSource.essence, maxEssence, maxEssence)),
    maxVitality,
    maxSpirit,
    maxEssence,
  }
}

export function fullVitals(attributes: Record<AttributeKey, Attribute>): Vitals {
  const maxima = deriveVitalMaximums(attributes)
  return {
    vitality: maxima.maxVitality,
    spirit: maxima.maxSpirit,
    essence: maxima.maxEssence,
    ...maxima,
  }
}

export function applyVitalDelta(vitals: Vitals, key: VitalKey, delta: number): void {
  const max = getVitalMax(vitals, key)
  vitals[key] = clampVitalValue(vitals[key] + Number(delta || 0), max, vitals[key])
}

export function vitalPercent(vitals: Pick<Vitals, VitalKey | keyof Pick<Vitals, 'maxVitality' | 'maxSpirit' | 'maxEssence'>>, key: VitalKey): number {
  const max = getVitalMax(vitals, key)
  if (max <= 0) return 0
  return Math.max(0, Math.min(100, Math.round((clampVitalValue(vitals[key], max, 0) / max) * 100)))
}

export function vitalRatio(vitals: Pick<Vitals, VitalKey | keyof Pick<Vitals, 'maxVitality' | 'maxSpirit' | 'maxEssence'>>, key: VitalKey): number {
  return vitalPercent(vitals, key) / 100
}

export function formatVitalValue(vitals: Pick<Vitals, VitalKey | keyof Pick<Vitals, 'maxVitality' | 'maxSpirit' | 'maxEssence'>>, key: VitalKey): string {
  return `${clampVitalValue(vitals[key], getVitalMax(vitals, key), 0)}/${getVitalMax(vitals, key)}`
}

export function vitalToneFromPercent(percent: number): VitalTone {
  if (percent > 50) return 'high'
  if (percent > 25) return 'mid'
  return 'low'
}

export function vitalTone(vitals: Pick<Vitals, VitalKey | keyof Pick<Vitals, 'maxVitality' | 'maxSpirit' | 'maxEssence'>>, key: VitalKey): VitalTone {
  return vitalToneFromPercent(vitalPercent(vitals, key))
}

export function describeVital(vitals: Pick<Vitals, VitalKey | keyof Pick<Vitals, 'maxVitality' | 'maxSpirit' | 'maxEssence'>>, key: VitalKey): { label: string, percent: number, tone: VitalTone } {
  const percent = vitalPercent(vitals, key)
  return {
    label: formatVitalValue(vitals, key),
    percent,
    tone: vitalToneFromPercent(percent),
  }
}

export function describeVitalScalar(value: unknown, max: unknown): { label: string, percent: number, tone: VitalTone } {
  const safeMax = Math.max(1, Number(max ?? value ?? 1))
  const safeValue = clampVitalValue(value, safeMax, 0)
  const percent = Math.max(0, Math.min(100, Math.round((safeValue / safeMax) * 100)))
  return {
    label: `${safeValue}/${safeMax}`,
    percent,
    tone: vitalToneFromPercent(percent),
  }
}
